import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Typography, Button, Space, Spin, Modal } from 'antd';
import {
  SearchOutlined, ToolOutlined, BarChartOutlined,
  RadarChartOutlined, RocketOutlined, DollarOutlined,
  GiftOutlined, SendOutlined, CheckCircleOutlined,
  ArrowRightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { billingAPI } from '../services/api';

const { Title, Text, Paragraph } = Typography;

const FEATURES = [
  {
    key: 'scanner',
    title: 'AEO Scanner',
    subtitle: 'AI 搜尋友好度偵測',
    icon: <SearchOutlined style={{ fontSize: 48, color: '#722ed1' }} />,
    color: '#722ed1', bg: '#f9f0ff', border: '#d3adf7', route: '/aeo-scanner',
    desc: '輸入任何網站網址，自動偵測 robots.txt、sitemap、llms.txt、JSON-LD 結構化資料，AI 分析並評分，提供完整行動建議。',
    features: [
      '爬蟲友好度檢測（robots.txt / sitemap / llms.txt）',
      '內容品質評分（JSON-LD / FAQ Schema / Meta）',
      'AI 能見度評估（ChatGPT / Perplexity / Gemini）',
      '完整改善建議與行動清單',
    ],
  },
  {
    key: 'fix',
    title: 'AEO Auto-Fix',
    subtitle: 'AI 自動修復',
    icon: <ToolOutlined style={{ fontSize: 48, color: '#1890ff' }} />,
    color: '#1890ff', bg: '#e6f7ff', border: '#91d5ff', route: '/aeo-fix',
    desc: '基於 AEO 掃描結果，透過 SSH 連線到目標伺服器，AI 自動生成並部署修復內容，系統會自動備份確保可回滾。',
    features: [
      'AI 自動產生修復程式碼（robots.txt / sitemap / JSON-LD）',
      'SSH 自動部署到目標伺服器',
      '修復前自動備份，支援一鍵回滾',
      '修復歷程完整記錄',
    ],
  },
  {
    key: 'citation',
    title: 'AI 引用監測',
    subtitle: 'Citation Monitor',
    icon: <BarChartOutlined style={{ fontSize: 48, color: '#52c41a' }} />,
    color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', route: '/citation-monitor',
    desc: '監測你的品牌在各大 AI 搜尋引擎（ChatGPT、Perplexity、Gemini）的引用頻率與情緒，定期追蹤競品動向。',
    features: [
      '品牌引用頻率追蹤與趨勢分析',
      '多平台覆蓋（ChatGPT / Perplexity / Gemini）',
      '情緒分析（正面 / 中立 / 負面）',
      '定期自動監測與報告',
    ],
  },
];

export default function AeoLandingPage() {
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [gateModal, setGateModal] = useState(false);
  const [pendingRoute, setPendingRoute] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await billingAPI.getStatus();
        setHasAccess(res.data?.ai_seo_subscription_active === true);
      } catch { setHasAccess(false); }
      setChecking(false);
    })();
  }, []);

  const handleFeatureClick = (feat) => {
    if (hasAccess) {
      navigate(feat.route);
    } else {
      setPendingRoute(feat);
      setGateModal(true);
    }
  };

  if (checking) {
    return (
      <div style={{ textAlign: 'center', padding: 80 }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}><Text type="secondary">載入中...</Text></div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ textAlign: 'center', padding: '40px 0 32px' }}>
        <RadarChartOutlined style={{ fontSize: 64, color: '#722ed1', marginBottom: 16 }} />
        <Title level={2} style={{ margin: 0 }}>AI SEO 工具集</Title>
        <Paragraph type="secondary" style={{ fontSize: 16, marginTop: 8 }}>
          全面提升你的品牌在 AI 搜尋引擎中的能見度
        </Paragraph>
      </div>

      <Row gutter={[24, 24]}>
        {FEATURES.map((feat) => (
          <Col xs={24} md={8} key={feat.key}>
            <Card hoverable style={{ borderRadius: 12, borderColor: feat.border, height: '100%', background: feat.bg }}
              onClick={() => handleFeatureClick(feat)}>
              <div style={{ textAlign: 'center', marginBottom: 16 }}>{feat.icon}</div>
              <Title level={4} style={{ textAlign: 'center', color: feat.color, margin: 0 }}>{feat.title}</Title>
              <Text type="secondary" style={{ display: 'block', textAlign: 'center', marginBottom: 12 }}>{feat.subtitle}</Text>
              <Paragraph style={{ fontSize: 13, marginBottom: 16 }}>{feat.desc}</Paragraph>
              <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, color: '#555' }}>
                {feat.features.map((f, i) => (<li key={i} style={{ marginBottom: 4 }}>{f}</li>))}
              </ul>
              <div style={{ textAlign: 'center', marginTop: 16 }}>
                <Button type="primary" ghost style={{ borderColor: feat.color, color: feat.color }}>
                  開始使用 <ArrowRightOutlined />
                </Button>
              </div>
            </Card>
          </Col>
        ))}
      </Row>

      <Card style={{ marginTop: 24, textAlign: 'center', background: '#fafafa' }}>
        {hasAccess ? (
          <Space>
            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 20 }} />
            <Text strong style={{ color: '#52c41a', fontSize: 16 }}>您已有有效訂閱，可直接使用所有 AI SEO 功能</Text>
          </Space>
        ) : (
          <div>
            <RocketOutlined style={{ fontSize: 32, color: '#fa8c16', marginBottom: 8 }} />
            <Title level={4} style={{ margin: '0 0 8px' }}>需要訂閱或優惠券才能使用</Title>
            <Paragraph type="secondary" style={{ marginBottom: 16 }}>
              AI SEO 功能使用 AI 模型（Claude / DeepSeek）分析網站，屬於進階功能。
            </Paragraph>
            <Space direction="vertical" size="small" style={{ width: '100%', maxWidth: 400 }}>
              <Button type="primary" size="large" icon={<DollarOutlined />} block onClick={() => navigate('/billing')}>前往訂閱方案</Button>
              <Button size="large" icon={<GiftOutlined />} block onClick={() => navigate('/billing')}>兌換優惠券</Button>
              <Button size="large" icon={<SendOutlined />} block onClick={() => navigate('/coupon-request')}>申請免費優惠券</Button>
            </Space>
          </div>
        )}
      </Card>

      <Modal title="需要訂閱或優惠券" open={gateModal} onCancel={() => setGateModal(false)} footer={null} width={420}>
        {pendingRoute && (
          <div style={{ textAlign: 'center', padding: '8px 0' }}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>{pendingRoute.icon}</div>
            <Title level={4}>{pendingRoute.title}</Title>
            <Paragraph type="secondary">使用此功能需要有效訂閱或優惠券。</Paragraph>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Button type="primary" size="large" icon={<DollarOutlined />} block onClick={() => { setGateModal(false); navigate('/billing'); }}>前往訂閱方案</Button>
              <Button size="large" icon={<GiftOutlined />} block onClick={() => { setGateModal(false); navigate('/billing'); }}>兌換優惠券</Button>
              <Button size="large" icon={<SendOutlined />} block onClick={() => { setGateModal(false); navigate('/coupon-request'); }}>申請免費優惠券</Button>
            </Space>
          </div>
        )}
      </Modal>
    </div>
  );
}
